import React from "react";
import { isNullOrWhiteSpace, replaceWithUnicode } from "../shared/string";
import {
  PlaceholderTextMap,
  replacePlaceholders,
  replacePortableTextPlaceholders
} from "../utilities/placeholderText";
import { PortableTextBody } from "./PortableText";
import PortableText, {
  getPortableTextColorTheme,
  PortableTextColorTheme
} from "./PortableText/PortableText";

export interface FaqItemProps {
  answer: PortableTextBody;
  question: string;
}

export interface PlaceholderText {
  placeholder: string;
  text: string;
}

export interface FaqTemplate {
  faqs?: FaqItemProps[];
  subtitle?: string;
  title?: string;
}

export interface FaqListProps extends FaqTemplate {
  faqTemplate?: FaqTemplate;
  placeholderTextMap?: PlaceholderText[] | PlaceholderTextMap;
  portableTextColorTheme?: PortableTextColorTheme;
  useTemplate?: boolean;
}

const replaceFaqPlaceholders = (
  faqs: FaqItemProps[],
  placeholderTextMap: PlaceholderTextMap
): FaqItemProps[] => {
  if (!placeholderTextMap || Object.keys(placeholderTextMap).length === 0) {
    return faqs;
  }

  return faqs.map(faq => ({
    question: replacePlaceholders(faq.question, placeholderTextMap),
    answer: replacePortableTextPlaceholders(faq.answer, placeholderTextMap)
  }));
};

const mapPlaceholderTextArrayToPlaceholderTextMap = (
  arr: PlaceholderText[]
): PlaceholderTextMap =>
  arr.reduce((map, item) => {
    if (!isNullOrWhiteSpace(item.placeholder)) {
      map[item.placeholder] = item.text;
    }
    return map;
  }, {});

const FaqList = ({
  faqs: blockFaqs,
  faqTemplate,
  portableTextColorTheme = PortableTextColorTheme.None,
  placeholderTextMap,
  subtitle: blockSubtitle,
  title: blockTitle,
  useTemplate = false
}: FaqListProps) => {
  const { elementClasses } = getPortableTextColorTheme(portableTextColorTheme);
  const map = Array.isArray(placeholderTextMap)
    ? mapPlaceholderTextArrayToPlaceholderTextMap(placeholderTextMap)
    : placeholderTextMap;

  const faqsToUse = useTemplate ? faqTemplate?.faqs : blockFaqs;
  const title = useTemplate ? faqTemplate?.title : blockTitle;
  const subtitle = useTemplate ? faqTemplate?.subtitle : blockSubtitle;

  const faqs = map ? replaceFaqPlaceholders(faqsToUse ?? [], map) : faqsToUse;

  return (
    <aside itemScope itemType="https://schema.org/FAQPage">
      {!isNullOrWhiteSpace(title) && (
        // Render with innerHTML to allow using "&shy;"
        <h2 className="text-current">{replaceWithUnicode(title)}</h2>
      )}

      {!isNullOrWhiteSpace(subtitle) && (
        <p className={`${elementClasses?.p ?? "text-current"} text-lg lg:text-2xl`}>
          {subtitle}
        </p>
      )}

      <ul className="border-t border-b py-5 mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-10 md:py-8 md:mt-8 xl:gap-x-16">
        {faqs
          ?.filter(f => !isNullOrWhiteSpace(f?.question))
          .map((faq, index) => (
            <li
              key={index}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
            >
              <h3 className="text-current text-xl lg:text-2xl" itemProp="name">
                {replaceWithUnicode(faq.question)}
              </h3>

              <div
                className="mt-4"
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
              >
                <div itemProp="text">
                  <PortableText
                    _rawBody={faq.answer}
                    addSections={false}
                    colorTheme={portableTextColorTheme}
                    elementClasses={elementClasses}
                  />
                </div>
              </div>
            </li>
          ))}
      </ul>
    </aside>
  );
};

export default FaqList;
